// The editing surface, shared by all three layouts — whichever wins, this stays.
import { cn } from '@my/ui';
import { Trash2 } from 'lucide-react';
import {
  polarityOf,
  requirementId,
  requirementMeta,
  POLARITY_LABEL,
  SET_BY_LABEL,
  SEVERITY_LABEL,
  type Polarity,
  type Requirement,
  type RequirementKind,
  type Severity,
  type Subject,
  type WorkflowTransition,
} from '../lib/mockWorkflow';
import { resolveOrderReferenceLabel } from '@/utils/orderReferenceLabels';
import { AddConditionMenu } from './AddConditionMenu';

const SEVERITIES: Severity[] = ['ignore', 'warn', 'stop'];

const SEVERITY_STYLE: Record<Severity, string> = {
  ignore: 'bg-slate-200 text-slate-700',
  warn: 'bg-amber-100 text-amber-900',
  stop: 'bg-red-100 text-red-800',
};

export interface RequirementListProps {
  title: string;
  /** Optional second line — used to say which moves a shared policy covers. */
  subtitle?: string;
  subject: Subject;
  transition: WorkflowTransition;
  /**
   * The organisation's order reference labels, positional — index 0 is
   * `Reference1`. Drives the reference picker, so it offers exactly the slots
   * this organisation has configured and calls them what the operator calls
   * them.
   */
  referenceLabels?: string[];
  /**
   * States the `fromState` check may name. Passed in because they are
   * track-local — this component only knows the subject otherwise.
   */
  states?: { key: string; label: string }[];
  /**
   * True where this move ABANDONS the record rather than advancing it. Flips
   * the wording and the default direction of a new condition: nothing has to be
   * true to cancel an order, so the useful rules are the ones that stop it.
   */
  abandons?: boolean;
  onChange: (next: WorkflowTransition) => void;
}

export function RequirementList({
  title,
  subtitle,
  subject,
  transition,
  states = [],
  referenceLabels = [],
  abandons = false,
  onChange,
}: RequirementListProps) {
  // Keyed on kind + direction, not kind alone: a check usable both ways could
  // legitimately sit on one transition twice.
  const patch = (id: string, changes: Partial<Requirement>) =>
    onChange({
      ...transition,
      requirements: transition.requirements.map((r) =>
        requirementId(r) === id ? { ...r, ...changes } : r,
      ),
    });

  const remove = (id: string) =>
    onChange({
      ...transition,
      requirements: transition.requirements.filter((r) => requirementId(r) !== id),
    });

  const add = (kind: RequirementKind, polarity: Polarity) => {
    const meta = requirementMeta(kind);
    onChange({
      ...transition,
      // New conditions arrive as `warn`, never `stop`. Adding a rule that
      // instantly blocks live work is the kind of surprise that gets a feature
      // switched off — the operator promotes it deliberately.
      requirements: [
        ...transition.requirements,
        { kind, severity: 'warn', polarity, param: meta.param },
      ],
    });
  };

  const blocking = transition.requirements.filter((r) => r.severity === 'stop').length;

  return (
    <div className="flex h-full flex-col rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-2">
        <div className="min-w-0">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
          {subtitle && <p className="mt-0.5 text-[11px] text-slate-400">{subtitle}</p>}
        </div>
        <span className="flex shrink-0 items-center gap-2 text-[11px] text-slate-400">
          {/* Who performs the move changes who the conditions protect: an
              operator can go and fix the record, the driver app cannot. */}
          {transition.setBy && transition.setBy !== 'operator' && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">
              {SET_BY_LABEL[transition.setBy]}
            </span>
          )}
          {transition.requirements.length === 0
            ? 'No conditions — always allowed'
            : `${blocking} blocking`}
        </span>
      </div>

      {transition.requirements.length === 0 ? (
        <p className="flex-1 px-4 py-6 text-center text-xs text-slate-400">
          {abandons
            ? 'Nothing prevents this — the move is always allowed. Add a condition to restrict it.'
            : 'Nothing required. Add a condition to make this move conditional.'}
        </p>
      ) : (
        <ul className="flex-1 divide-y divide-slate-100">
          {transition.requirements.map((r) => (
            <RequirementRow
              key={requirementId(r)}
              requirement={r}
              states={states}
              referenceLabels={referenceLabels}
              onPatch={(changes) => patch(requirementId(r), changes)}
              onRemove={() => remove(requirementId(r))}
            />
          ))}
        </ul>
      )}

      <div className="border-t border-slate-200 px-4 py-2">
        <AddConditionMenu
          subject={subject}
          preferred={abandons ? 'prevent' : 'require'}
          alreadyAdded={transition.requirements.map(requirementId)}
          onAdd={add}
        />
      </div>
    </div>
  );
}

function RequirementRow({
  requirement,
  states,
  referenceLabels,
  onPatch,
  onRemove,
}: {
  requirement: Requirement;
  states: { key: string; label: string }[];
  referenceLabels: string[];
  onPatch: (changes: Partial<Requirement>) => void;
  onRemove: () => void;
}) {
  const meta = requirementMeta(requirement.kind);

  return (
    <li
      className={cn(
        'group flex flex-wrap items-center gap-2 px-4 py-2.5',
        // A switched-off rule recedes but stays visible: deleting it would lose
        // the operator's memory that the check exists at all.
        requirement.severity === 'ignore' && 'opacity-55',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {/* Direction first: "Prevents · Already invoiced" is unambiguous,
              where a bare "Already invoiced" could be read either way. */}
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
              polarityOf(requirement) === 'prevent'
                ? 'bg-rose-50 text-rose-700'
                : 'bg-sky-50 text-sky-700',
            )}
          >
            {POLARITY_LABEL[polarityOf(requirement)]}
          </span>
          <p className="text-sm text-slate-800">{meta.label}</p>
          {meta.crossTrack && (
            <span className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700">
              depends on the other track
            </span>
          )}
          <ParamEditor
            requirement={requirement}
            states={states}
            referenceLabels={referenceLabels}
            onPatch={onPatch}
          />
        </div>
        <p className="mt-0.5 text-[11px] text-slate-400">{meta.detail}</p>
      </div>

      {/* Three-way, matching the invoice-approval policy's vocabulary rather
          than inventing a second one. */}
      <div className="flex shrink-0 overflow-hidden rounded-md border border-slate-200">
        {SEVERITIES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPatch({ severity: s })}
            className={cn(
              'px-2 py-0.5 text-[11px] transition-colors',
              requirement.severity === s
                ? SEVERITY_STYLE[s]
                : 'bg-white text-slate-400 hover:bg-slate-50',
            )}
          >
            {SEVERITY_LABEL[s]}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${meta.label}`}
        className="shrink-0 text-slate-300 opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}

/**
 * The parameter, edited in place. Which control appears is driven by the kind —
 * a reference check needs a slot, a count needs a number, and a task-type check
 * needs the set of types that count.
 */
function ParamEditor({
  requirement,
  states,
  referenceLabels,
  onPatch,
}: {
  requirement: Requirement;
  states: { key: string; label: string }[];
  referenceLabels: string[];
  onPatch: (changes: Partial<Requirement>) => void;
}) {
  const chip =
    'rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] text-slate-700 hover:border-slate-300';

  switch (requirement.kind) {
    case 'customerReference':
      // Options are the slots THIS organisation configured (Settings →
      // Organisation → Order Reference Labels), and the value stored is the
      // 1-based slot rather than the label — so renaming a field keeps the
      // rule pointing at the same column.
      return (
        <select
          value={requirement.param ?? '1'}
          onChange={(e) => onPatch({ param: e.target.value })}
          className={chip}
          aria-label="Which reference field"
        >
          {referenceLabels.map((_, i) => (
            <option key={i} value={String(i + 1)}>
              {resolveOrderReferenceLabel(referenceLabels, i)}
            </option>
          ))}
        </select>
      );

    case 'minTasks':
    case 'hasLines':
      return (
        <input
          type="number"
          min={1}
          max={20}
          value={requirement.param ?? '1'}
          onChange={(e) => onPatch({ param: e.target.value })}
          className={cn(chip, 'w-14 text-right')}
          aria-label="Minimum number of tasks"
        />
      );

    case 'fromState': {
      // ONE selection, because it is a threshold: states run in order, so
      // blocking from In progress necessarily blocks Completed too. The chips
      // show that — the picked state is solid and everything downstream is
      // tinted, so the operator sees the reach of the rule before saving it.
      const picked = (requirement.param ?? '').trim();
      const at = states.findIndex((st) => st.key === picked);
      return (
        <span className="flex flex-wrap items-center gap-1">
          {states.map((st, i) => {
            const isPicked = at >= 0 && i === at;
            const isCovered = at >= 0 && i > at;
            return (
              <button
                key={st.key}
                type="button"
                // Clicking the current pick clears it, so a rule can be
                // switched off without deleting the row.
                onClick={() => onPatch({ param: isPicked ? '' : st.key })}
                title={
                  isCovered
                    ? `Covered because it comes after ${states[at]?.label}`
                    : `Block cancelling from ${st.label} onwards`
                }
                className={cn(
                  'rounded px-1.5 py-0.5 text-[11px] ring-1',
                  isPicked
                    ? 'bg-rose-100 font-medium text-rose-900 ring-rose-300'
                    : isCovered
                      ? 'bg-rose-50/60 text-rose-500 ring-rose-100'
                      : 'bg-white text-slate-400 ring-slate-200 hover:bg-slate-50',
                )}
              >
                {st.label}
              </button>
            );
          })}
          {at >= 0 && <span className="text-[10px] text-slate-400">onwards</span>}
        </span>
      );
    }

    default:
      return null;
  }
}
